import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Text, useTheme } from '../ui';
import { Close as CloseIcon } from '../ui/icons';
import { CheckCircleOutline as CheckCircleOutlineIcon } from '../ui/icons';
import { ErrorOutline as ErrorOutlineIcon } from '../ui/icons';
import { WarningAmber as WarningAmberIcon } from '../ui/icons';
import { InfoOutlined as InfoOutlinedIcon } from '../ui/icons';
import { useToastContext } from '../contexts/useToast';
import type { Toast, ToastSeverity } from '../contexts/ToastContext';

const severityIcon: Record<ToastSeverity, React.ReactElement> = {
  success: <CheckCircleOutlineIcon fontSize="small" />,
  error:   <ErrorOutlineIcon fontSize="small" />,
  warning: <WarningAmberIcon fontSize="small" />,
  info:    <InfoOutlinedIcon fontSize="small" />,
};

const keyframesStyle = `
@keyframes slideInRight {
  from { transform: translateX(110%); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
}
@keyframes shrinkProgress {
  from { width: 100%; }
  to   { width: 0%;   }
}
`;

interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onRemove }) => {
  const theme = useTheme();
  const [visible, setVisible] = useState(true);

  const colorMap: Record<ToastSeverity, string> = {
    success: theme.palette.success.main,
    error:   theme.palette.error.main,
    warning: theme.palette.warning.main,
    info:    theme.palette.info?.main ?? theme.palette.primary.main,
  };

  const accentColor = colorMap[toast.severity];

  useEffect(() => {
    if (!visible) {
      const t = setTimeout(() => onRemove(toast.id), 300);
      return () => clearTimeout(t);
    }
  }, [visible, toast.id, onRemove]);

  return (
    <Box
      // Announced, not just drawn. A toast is how this platform says what
      // happened -- saved, sent, ended -- and until now it said it to sighted
      // readers alone: the message appears, is read by nobody, and is gone in
      // five seconds. 'alert' for a failure, because a refusal interrupts; a
      // polite status for the rest, so a confirmation waits its turn.
      role={toast.severity === 'error' ? 'alert' : 'status'}
      aria-live={toast.severity === 'error' ? 'assertive' : 'polite'}
      styles={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 280,
        maxWidth: 380,
        backgroundColor: 'background.paper',
        borderRadius: 1,
        boxShadow: 3,
        overflow: 'hidden',
        animation: 'slideInRight 0.25s ease-out',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0)' : 'translateX(110%)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        borderLeft: `4px solid ${accentColor}`,
      }}
    >
      {/* Content row */}
      <Box styles={{ display: 'flex', alignItems: 'flex-start', px: 1.5, pt: 1.25, pb: 1, gap: 1 }}>
        <Box styles={{ color: accentColor, mt: '2px', flexShrink: 0 }}>
          {severityIcon[toast.severity]}
        </Box>
        <Text level="body2" styles={{ flex: 1, lineHeight: 1.5, whiteSpace: 'pre-line' }}>
          {toast.message}
        </Text>
        {toast.action && (
          <Button
            size="sm"
            emphasis="plain"
            onClick={() => {
              toast.action?.onClick();
              setVisible(false);
            }}
            styles={{ mt: '-4px', flexShrink: 0, fontWeight: 700 }}
          >
            {toast.action.label}
          </Button>
        )}
        <IconButton
          size='sm'
          onClick={() => setVisible(false)}
          styles={{ ml: 0.5, mt: '-4px', color: 'text.secondary' }}
          aria-label="close"
        >
          <CloseIcon styles={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Progress bar */}
      <Box
        styles={{
          height: 3,
          backgroundColor: accentColor,
          animationName: 'shrinkProgress',
          animationDuration: `${toast.duration}ms`,
          animationTimingFunction: 'linear',
          animationFillMode: 'forwards',
        }}
      />
    </Box>
  );
};

const ToastContainer: React.FC = () => {
  const { toasts, removeToast, toastRightOffset } = useToastContext();

  return (
    <>
      <style>{keyframesStyle}</style>
      <Box
        vendorStyles={{
          position: 'fixed',
          bottom: 24,
          right: 24 + toastRightOffset,
          transition: 'right 0.2s ease',
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column-reverse',
          gap: 1,
          pointerEvents: 'none',
          '& > *': { pointerEvents: 'all' },
        }}
      >
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </Box>
    </>
  );
};

export default ToastContainer;
